import { FC } from "react";
import { createPortal } from "react-dom";
import { X, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AddNote } from "./AddNote";
import { Note } from "./Note/Note";

type NotesModalProps = {
	structureId: Dryp.StructureId;
	notes: Dryp.Note[];
	loading: boolean;
	error: ApiError | null;
	canDelete?: boolean;
	onClose: () => void;
	onNoteAdded: (note: Dryp.Note) => void;
	onNoteDeleted?: (noteId: Dryp.NoteId) => Promise<void>;
};

export const NotesModal: FC<NotesModalProps> = ({
	structureId,
	notes,
	loading,
	error,
	canDelete,
	onClose,
	onNoteAdded,
	onNoteDeleted,
}) =>
	createPortal(
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
			<div
				className="relative w-[90vw] max-w-2xl h-[90vh] rounded-lg overflow-hidden bg-card flex flex-col"
				onClick={e => e.stopPropagation()}
			>
				<div className="p-4 border-b border-border flex items-center justify-between shrink-0">
					<h3 className="text-sm font-medium flex items-center gap-2">
						<MessageSquare className="w-4 h-4" />
						Notes
					</h3>
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
						<X className="w-4 h-4" />
					</Button>
				</div>

				<div className="p-4 border-b border-border shrink-0">
					<AddNote structureId={structureId} onPosted={onNoteAdded} />
				</div>

				<div className="flex-1 overflow-y-auto p-4">
					{loading && (
						<div className="space-y-4">
							<Skeleton className="h-12 w-full" />
							<Skeleton className="h-12 w-full" />
						</div>
					)}

					{error && <div className="text-sm text-red-500">Failed to load notes</div>}

					{!loading && !error && notes.length === 0 && (
						<div className="text-muted-foreground text-sm">No notes yet.</div>
					)}

					{notes.length > 0 && (
						<div className="relative">
							<div className="absolute left-[9px] top-3 bottom-3 w-px bg-border z-0" />
							<div className="space-y-4">
								{notes.map(n => (
									<Note key={n.id} note={n} canDelete={canDelete} onDelete={onNoteDeleted} />
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
